"use client"

import type { Settings as LayoutSettings } from "@ant-design/pro-components"
import type { RunTimeLayoutConfig } from "@umijs/max"
import { history, Link, useDispatch, useLocation } from "@umijs/max"
import defaultSettings from "../config/defaultSettings"
import { errorConfig } from "./requestErrorConfig"
import { useEffect, useState, useRef } from "react"
import { currentUser, logout } from "./pages/User/Login/service"
import { DollarOutlined, UserOutlined, CustomerServiceOutlined, LogoutOutlined } from "@ant-design/icons"
import { Flex, Menu, message } from "antd"
import AydentifyAnimation from "./components/animated_logo"
import { GoogleOAuthProvider } from '@react-oauth/google';

// Declare Zoho SalesIQ types
declare global {
  interface Window {
    $zoho?: {
      salesiq?: {
        ready: (callback: () => void) => void;
        visitor?: {
          name: (name: string) => void;
          email: (email: string) => void;
        };
        privacy?: {
          updateCookieConsent: (consentCategories: ('analytics' | 'performance')[]) => void;
          getCookieList?: () => Promise<string[]>;
        };
      };
    };
    clarity?: {
      q?: any[];
      (method: string, ...args: any[]): void;
    };
  }
}


// For Firebase JS SDK v7.20.0 and later, measurementId is optional
// const firebaseConfig = {
//   apiKey: "AIzaSyAiG9qdW_tnXjyyqzobxEcaeBmD64BZJQg",
//   authDomain: "scout-e78e3.firebaseapp.com",
//   projectId: "scout-e78e3",
//   storageBucket: "scout-e78e3.appspot.com",
//   messagingSenderId: "506508788463",
//   appId: "1:506508788463:web:fd5e170891958dcf27d11a",
//   measurementId: "G-9YLCJ9X56N",
// }

const loginPath = "/user/login"

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>
  currentUser?: any
  loading?: boolean
  fetchUserInfo?: () => Promise<any>
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await currentUser()
      return msg
    } catch (error) {
      history.push(loginPath)
    }
    return undefined
  }

  const { location } = history
  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo()
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    }
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  }
}

export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }: any) => {
  const { currentUser } = initialState
  const dispatch = useDispatch()

  const [isHovered, setIsHovered] = useState(false)
  const [collapsed, setCollapsed] = useState(true)
  const [loading, setLoading] = useState(false)
  const location = useLocation()
  
  // Track previous path to detect navigation away from search pages
  const prevPathRef = useRef<string>(location.pathname)

  // Effect to clear search data when navigating away from search pages
  useEffect(() => {
    const currentPath = location.pathname
    const prevPath = prevPathRef.current
    
    // Check if previously on a search page and now navigated away
    const wasOnSearchPage = prevPath.includes('/search')
                           
    const isSearchPage = currentPath.includes('/search')
    
    // If returning to search page from another page, clear search data
    if (isSearchPage && !wasOnSearchPage) {
      dispatch({ type: 'search/resetAll' })
      dispatch({ type: 'people/resetAll' })
    }
    
    // Update the previous path ref
    prevPathRef.current = currentPath
  }, [location.pathname, dispatch])

  // Initialize Microsoft Clarity
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.clarity) {
      const script = document.createElement('script');
      script.async = true;
      script.src = "https://www.clarity.ms/tag/ro30pmkzlo";
      document.head.appendChild(script);
      
      // Define clarity function
      (window as any).clarity = function(method: string, ...args: any[]) {
        (window as any).clarity.q = (window as any).clarity.q || [];
        (window as any).clarity.q.push([method, ...args]);
      };
    }
    
    return () => {
      // Cleanup Clarity script when component unmounts if needed
      const clarityScript = document.querySelector('script[src*="clarity.ms/tag"]');
      if (clarityScript) {
        clarityScript.remove();
      }
    };
  }, []);

  // Set Clarity user identification when user data is available
  useEffect(() => {
    if (currentUser && typeof window !== 'undefined' && window.clarity) {
      try {
        // random session id 
        const sessionId = Math.random().toString(36).substring(2, 15);
        window.clarity("identify", currentUser.id, `${currentUser.id}-${sessionId}`, `app-${currentUser.id}-${sessionId}`, currentUser.fullname);
        
        console.log('Clarity has started for user', currentUser.id);
      } catch (error) {
        console.log('Error initializing Clarity:', error);
      }
      // Check if the script is already added
      if (!document.getElementById('zsiqscript')) {
        // Initialize Zoho variables first
        window.$zoho = window.$zoho || {};
        window.$zoho.salesiq = window.$zoho.salesiq || {ready: function(){}};
        
        // Create the script element
        const script = document.createElement('script');
        script.id = 'zsiqscript';
        script.src = "https://salesiq.zohopublic.in/widget?wc=siq0890e99a95f076ec133f1574533859619a685f41f1ad2f76122e00a47dd1f7b80b081506899f635f0171f77277e07459";
        script.defer = true;
        
        // Append to document head
        document.head.appendChild(script);
      }
      
      // Set visitor information when Zoho is ready
      setTimeout(() => {
        if (window.$zoho && window.$zoho.salesiq) {
          window.$zoho?.salesiq?.visitor?.name(currentUser.fullname);
          window.$zoho?.salesiq?.visitor?.email(currentUser.email);

          if (window.$zoho?.salesiq?.privacy?.updateCookieConsent) {
            window.$zoho.salesiq.privacy.updateCookieConsent(['analytics', 'performance']);
            console.log('Zoho SalesIQ cookie consent accepted programmatically.');
          } else {
            console.log('Zoho SalesIQ cookie consent not accepted programmatically.');
          }
        }
      }, 1000);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      dispatch({
        type: "subscription/getCurrentPlan",
      })
    }
  }, [currentUser, dispatch])

  useEffect(() => {
    const interval = setInterval(() => {
      const sider = document.querySelector(".ant-layout-sider")
      if (sider) {
        const hovered = sider.matches(":hover")
        if (hovered && !isHovered) {
          setIsHovered(true)
          setCollapsed(false)
        } else if (!hovered && isHovered) {
          setIsHovered(false)
          setCollapsed(true)
        }
      }
    }, 100)

    return () => clearInterval(interval)
  }, [isHovered])

  const handleLogout = async () => {
    try {
      setLoading(true)
      
      // Clean up Zoho SalesIQ if needed - remove script
      const zsiqScript = document.getElementById('zsiqscript');
      if (zsiqScript) {
        zsiqScript.remove();
      }
      
      // Remove any Zoho elements
      document.querySelectorAll('[id^="zsiq_"]').forEach(el => el.remove());
      
      await logout()
      setInitialState((s: any) => ({ ...s, currentUser: undefined }))
      history.push(loginPath)
    } catch (error: any) {
      message.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const bottomMenuItems = [
    {
      key: "pricing",
      icon: <DollarOutlined />,
      label: "Pricing",
      onClick: () => history.push("/pricing"),
    },
    {
      key: "profile",
      icon: <UserOutlined />,
      label: currentUser?.fullname,
      onClick: () => history.push("/profile"),
    },
    {
      key: "support",
      icon: <CustomerServiceOutlined />,
      label: "Support",
      onClick: () => history.push("/support"),
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Log Out",
      onClick: handleLogout,
      disabled: loading,
    },
  ]

  return {
    actionsRender: () => [],
    token: {
      bgLayout: "#fff",
      pageContainer: {
        paddingBlockPageContainerContent: 0,
        paddingInlinePageContainerContent: 0,
      },
    },
    onCollapse: (collapsed: boolean) => {
      setCollapsed(collapsed)
    },
    siderWidth: 220,
    title: false,
    collapsed: collapsed,
    defaultCollapsed: true,
    breakpoint: false,
    className: "layout-root",
    style: {
      height: "100vh",
      display: "flex",
      flexDirection: "column",
    },
    collapsedButtonRender: false,
    logo: <AydentifyAnimation isExpanded={!collapsed} />,
    logoStyle: {
      backgroundColor: "#FFE500",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "64px", // Adjust this value as needed
    },
    menuContentRender: (props, defaultDom) => {
      return (
        <Flex vertical justify="space-between" style={{ height: "100%" }}>
          <Menu
            mode="inline"
            inlineCollapsed={collapsed}
            items={props.menuData?.map((item: any) => ({
              key: item.path,
              icon: item.icon,
              label: <Link to={item.path}>{item.name}</Link>,
              style: {
                paddingLeft: 12,
                marginLeft: 4,
              },
            }))}
            selectedKeys={[location.pathname]}
          />
          <Menu
            mode="inline"
            inlineCollapsed={collapsed}
            items={bottomMenuItems.map((item) => ({
              ...item,
              label: item.key === 'logout' ? item.label : <Link to={item.key}>{item.label}</Link>,
              style: {
                paddingLeft: 12,
                marginLeft: 4,
              },
            }))}
            selectedKeys={[location.pathname.replace("/", "")]}
            style={{
              borderTop: "1px solid #f0f0f0",
            }}
          />
        </Flex>
      )
    },
    onPageChange: () => {
      const { location } = history
      
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath)
      }
    },

    childrenRender: (children: any) => {
      return (
        <>
          {children}
        </>
      )
    },
    ...initialState?.settings,
  }
}

export const request = {
  ...errorConfig,
}

export function rootContainer(container: React.ReactNode) {
  return (
    <GoogleOAuthProvider clientId={'1048792957309-vo4h530et8rsq71ibi5568nkrp4kjkuo.apps.googleusercontent.com'}>
      {container}
    </GoogleOAuthProvider>
  );
}

